import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import useTypedContext from "hooks/useTypedContext";
import useKillRun from "shared/Run/useKillRun";
import { AccessResult } from "utils/access";
import Tooltip from "ds/components/Tooltip";

import { useRunShortcut } from "../../hooks/useRunShortcut";

type KillRunButtonProps = {
  runId: string;
  stackId: string;
  runQueryToRefetch?: string;
  canKill: AccessResult;
};

const KillRunButton = ({ runId, stackId, runQueryToRefetch, canKill }: KillRunButtonProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const [killRun, { loading, data }] = useKillRun({
    refetchQueries: [runQueryToRefetch || "GetRun"],
  });

  const onClick = useCallback(() => {
    killRun({ stack: stackId, run: runId, note: null, state: null }, (data) => {
      if (data?.runKill?.id) {
        reportSuccess({ message: `Run ${data.runKill.id} successfully force killed` });
      }
    });
  }, [killRun, stackId, runId, reportSuccess]);

  const isLoading = loading || !!data?.runKill?.id;

  useRunShortcut({
    label: "Force Kill",
    code: "mod+alt+k",
    callback: onClick,
    loading: isLoading,
    disabled: !canKill.result,
  });

  if (!canKill.result && !canKill.message) {
    return null;
  }

  return (
    <Tooltip
      active={!!canKill.message}
      on={({ ...props }) => (
        <Button
          {...props}
          variant="secondary"
          disabled={isLoading || !canKill.result}
          loading={isLoading}
          onPress={onClick}
          size="small"
        >
          Force Kill
        </Button>
      )}
    >
      {canKill.message}
    </Tooltip>
  );
};

export default KillRunButton;
